import React, { useState } from 'react';
import { Input, notification } from 'antd';
import { Form, Formik } from 'formik';
import Modal from '../../../components/Modal';
import { useActions } from '../../../context/Actions.context';
import { useQueryClient } from 'react-query';
import { useUnmuteAuthor } from '../../../core/modules/book/hooks/useUnmuteAuthor';
import showNotification from '../../../section/Common/utils/showNotification';
import { Title } from '../../../components/styles';
import { UNMUTEAUTHOR } from '../../../core/constants';
import { ReactComponent as MuteIcon } from '../../../assets/icons/bell-off-outline-mute.svg';
import { ReactComponent as UnmuteIcon } from '../../../assets/icons/bell-off-fill-unmute.svg';

const { TextArea } = Input;

interface IAuthorProps {
  ASIN: string;
  Author?: string;
}

interface IProps {
  book: IAuthorProps;
  modal: boolean;
  toggleModal: Function;
  id: string;
}

const UnmuteAuthorTitles: React.FC<IProps> = ({
  book,
  modal,
  toggleModal,
  id
}) => {
  const { Author } = book;
  const { isActionLoading, setActionLoading } = useActions();
  const { mutateAsync } = useUnmuteAuthor(UNMUTEAUTHOR);
  const queryClient = useQueryClient();
  const [note, setNote] = useState('');

  const handleClose = (resetForm?: Function) => {
    setNote('');
    resetForm?.();
    toggleModal();
  };

  const onUnmute = (_values, cb) => {
    if (isActionLoading) {
      return;
    }
    setActionLoading(true);

    try {
      const payload = {
        authors: [book.Author],
        note
      };

      const promise = mutateAsync(payload);

      cb.resetForm();

      showNotification(
        promise,
        'The Author was successfully unmuted.',
        'The Author could not be unmuted.',
        Author
      );

      promise.then(() => queryClient.refetchQueries(`get-row-count${id}`));
      promise.then(() => queryClient.refetchQueries(`get-${id}`));
      promise.then(() => {
        handleClose(cb.resetForm);
      });
      promise.finally(() => setActionLoading(false));
    } catch (error) {
      handleClose(cb.resetForm);

      notification.error({
        description: 'Please try again.',
        message: 'Failed to unmute author'
      });

      setActionLoading(false);

      console.error('Failed to unmute author', {
        author: Author,
        error
      });
    }
  };

  return (
    <Formik
      initialValues={{}}
      enableReinitialize={true}
      onSubmit={onUnmute}
    >
      {({ handleSubmit, resetForm }) => (
        <Form>
          <Modal
            title='Unmute Author'
            visible={modal}
            onOk={handleSubmit}
            okText='Unmute Author'
            onCancel={() => handleClose(resetForm)}
            confirmLoading={isActionLoading}
            okButtonProps={{ disabled: isActionLoading }}
          >
            <Title>
              <p style={{ fontWeight: 500 }}>
                Following author will be unmuted <UnmuteIcon
                  className='actions-icons actions-icons-unmute'
                  style={{ paddingTop: '5px' }} /> :
              </p>

              <p style={{ marginBottom: 18 }}>{Author}</p>

              <ul style={{ paddingLeft: 18, marginBottom: 16 }}>
                <li>When unmuted, these authors’ titles will be visible under {' '}
                  All New Titles or Hot Titles if their ranking criteria is met.
                </li>
                <li>
                  You can mute the author anytime again by clicking on {' '}
                  <MuteIcon
                    className='actions-icons actions-icons-mute'
                    style={{ paddingTop: '5px' }} /> Mute under Actions.
                </li>
              </ul>
            </Title>

            <TextArea
              placeholder='Leave a note for unmuting this author'
              maxLength={400}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={4}
            />

            <div style={{ textAlign: 'right', fontSize: 12, marginTop: 4 }}>
              {note.length} / 400
            </div>

          </Modal>
        </Form>
      )}
    </Formik>
  );
};

export default React.memo(UnmuteAuthorTitles);